/* eslint-disable react-refresh/only-export-components */
import { useLocalStorage } from "@/hooks/use-local-storage-2";
import React, { createContext, useCallback, useContext, useState } from "react";
import {
  format,
  parse,
  isValid,
  getMilliseconds,
  getSeconds,
  endOfSecond,
  Duration,
  intervalToDuration,
  differenceInMilliseconds,
  endOfMinute,
  DateArg,
} from "date-fns";
import { TZDateMini, tzOffset } from "@date-fns/tz";

export type TimeSettings = {
  preferAccountTimezone?: boolean;
  timezone?: "utc" | "local" | string;
};

type TimeSettingsProviderProps = {
  children?: React.ReactNode;
  defaultTimeSettings?: TimeSettings;
  storageKey?: string;
};

type TimeSettingsValue = {
  renderTime: <DateType extends Date>(
    date: DateArg<DateType> | undefined | null,
    options?: { withMs?: boolean },
  ) => string | undefined;
  parseRenderedTime: (date: string) => Date | null | undefined;
  col: string;
  timeZone?: string; // for date pickers
};

export type TimeSettingsProviderState = TimeSettings &
  TimeSettingsValue & {
    set: React.Dispatch<React.SetStateAction<TimeSettings>>;
    as: (timezone?: string) => TimeSettingsValue;
  };

const TimeSettingsProviderContext = createContext<TimeSettingsProviderState>(
  undefined as unknown as TimeSettingsProviderState,
);

export function getTimeZone(timezone: TimeSettings["timezone"]) {
  return timezone === "local"
    ? Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone
    : timezone === "utc"
      ? "UTC"
      : timezone;
}

export function getTimeZoneShort(timezone: TimeSettings["timezone"]) {
  const timeZone = timezone ? getTimeZone(timezone) : undefined;
  return timeZone ? numberToTz(tzOffset(timeZone, new Date())) : undefined;
}

// example: 120 -> +02:00
export const numberToTz = (tzn: number) => {
  const tznAbs = Math.abs(tzn);
  const tz = `${tzn < 0 ? "-" : "+"}${((tznAbs / 60) % 24).toString().padStart(2, "0")}:${(
    tznAbs % 60
  )
    .toString()
    .padStart(2, "0")}`;
  return tz;
};

export function calcFromTz(timezone: TimeSettings["timezone"]) {
  const timeZone = getTimeZone(timezone);
  const short = timeZone
    ? numberToTz(tzOffset(timeZone, new Date()))
    : undefined;
  const _col =
    timezone == "local"
      ? ""
      : timezone == "utc"
        ? "UTC"
        : timezone
          ? numberToTz(tzOffset(timezone, new Date()))
          : "";
  const col = _col == "" ? "" : ` (${_col})`;
  return { col, short, timeZone };
}

export function TimeSettingsProvider({
  children,
  defaultTimeSettings = { preferAccountTimezone: true, timezone: "local" },
  storageKey = "timeSettings",
  ...props
}: TimeSettingsProviderProps) {
  const [timeSettings, setTimeSettings] =
    useState<TimeSettings>(defaultTimeSettings);
  useLocalStorage(storageKey, setTimeSettings, timeSettings);
  const as = useCallback(
    (timezone?: string) => {
      const { col, timeZone } = calcFromTz(timezone ?? timeSettings.timezone);
      const renderTime = <DateType extends Date>(
        date: DateArg<DateType> | undefined | null,
        options?: { withMs?: boolean },
      ) => {
        if (!date) return undefined;
        const d = new TZDateMini(date as DateType, timeZone);
        const s = getSeconds(d);
        const ms = getMilliseconds(d);
        const fmt = `dd/MM/yyyy HH:mm:ss${(options?.withMs ?? ((ms > 0 && ms < 999) || (s >= 59 && ms < 999))) ? ".SSS" : ""}`;
        return format(d, fmt);
      };
      const parseRenderedTime = (time: string) => {
        const ref = new TZDateMini(new Date(), timeZone);
        let d = parse(time, "dd/MM/yyyy HH:mm:ss.SSS", ref);
        if (isValid(d)) {
          if (getMilliseconds(d) >= 999) return endOfSecond(d);
          return d;
        }
        d = parse(time, "dd/MM/yyyy HH:mm:ss", ref);
        if (isValid(d)) {
          if (getSeconds(d) >= 59) return endOfMinute(d);
          return d;
        }
        return undefined;
      };
      return { col, timeZone, renderTime, parseRenderedTime };
    },
    [timeSettings.timezone],
  );
  const self = React.useMemo(as, [as]);
  const value: TimeSettingsProviderState = {
    ...timeSettings,
    ...self,
    as,
    set: setTimeSettings,
  };
  return (
    <TimeSettingsProviderContext.Provider {...props} value={value}>
      {children}
    </TimeSettingsProviderContext.Provider>
  );
}

export const useTimeSettings = () => {
  const context = useContext(TimeSettingsProviderContext);

  if (context === undefined)
    throw new Error(
      "useTimeSettings must be used within a TimeSettingsProvider",
    );

  return context;
};

export const intervalToDurationMs = (
  ...args: Parameters<typeof intervalToDuration>
) => {
  const duration = intervalToDuration(...args);
  const [interval] = args;
  duration.seconds =
    (differenceInMilliseconds(interval.end, interval.start) / 1000) % 60;
  return duration;
};

export const differenceInMillisecondsToDuration = (dt: number): Duration => ({
  seconds: dt / 1000,
  minutes: Math.floor(dt / 60000), // 60 * 1000
  hours: Math.floor(dt / 1440000), // 24 * 60 * 1000
});

export const renderTime = <DateType extends Date>(
  d: DateArg<DateType> | undefined | null,
  options?: { withMs?: boolean },
) => {
  if (!d) return undefined;
  const s = getSeconds(d);
  const ms = getMilliseconds(d);
  const fmt = `dd/MM/yyyy HH:mm:ss${(options?.withMs ?? ((ms > 0 && ms < 999) || (s >= 59 && ms < 999))) ? ".SSS" : ""}`;
  return format(d, fmt);
};

export const renderDuration = (
  duration: Duration,
  options?: {
    withHours?: boolean;
    withMinutes?: boolean;
    withSeconds?: boolean;
    withMs?: boolean;
    withUnits?: boolean;
  },
) => {
  const seconds = Math.floor(duration?.seconds ?? 0);
  const ms = Math.floor(((duration.seconds ?? 0) - seconds) * 1000);
  return (
    ((options?.withHours ?? duration.hours)
      ? `${(duration.hours ?? 0).toString().padStart(2, "0")}${options?.withUnits ? "h " : ":"}`
      : "") +
    ((options?.withHours ?? duration.hours) ||
    (options?.withMinutes ?? duration.minutes)
      ? `${(duration.minutes ?? 0).toString().padStart(2, "0")}${options?.withUnits ? "m " : ":"}`
      : "") +
    ((options?.withHours ?? duration.hours) ||
    (options?.withMinutes ?? duration.minutes) ||
    (options?.withSeconds ?? seconds) ||
    (options?.withMs ?? ms)
      ? `${(seconds ?? 0).toString().padStart(2, "0")}${options?.withUnits ? "s " : ""}`
      : "") +
    ((options?.withMs ?? ms)
      ? `${options?.withUnits ? "" : "."}${ms.toString().padStart(3, "0")}${options?.withUnits ? "ms " : ""}`
      : "")
  ).trimEnd();
};
